import type { DialogNewProps } from 'components/dialog-new/props';
import type { FloatingAlertProps } from 'components/floating-alert/props';
import type { ToastMessageProps } from 'components/toast-message/props';
import { defineStore } from 'pinia';
import type {
	Component,
	ComponentInstance,
	ComputedRef,
	HTMLAttributes,
	MaybeRef,
	Raw,
	Slot,
} from 'vue';
import {
	markRaw,
	onBeforeUnmount,
	onMounted,
} from 'vue';
import type { Cons } from 'vue-facing-decorator/dist/component';

interface FallthroughAttributes {
	class?: unknown;
	id?: string;
	onClick?: (event: MouseEvent) => unknown;
	style?: unknown;
}

export type DialogProps = DialogNewProps & FallthroughAttributes & {
	'onClick:outside'?: (payload: MouseEvent | TouchEvent) => unknown;
	onClose?: (payload: KeyboardEvent | MouseEvent | TouchEvent) => unknown;
};

type FloatingAlertComponentProps = FloatingAlertProps & FallthroughAttributes & {
	onClose?: () => unknown;
};

type ToastComponentProps = ToastMessageProps & FallthroughAttributes & {
	'onClick:action'?: () => unknown;
	'onClick:close'?: () => unknown;
};

interface ComponentBase<TProps> {
	props?: ComponentValue<TProps>;
	visible?: ComponentValue<boolean>;
}

type ComponentWithId<T> = T & {
	id: string;
};

type DialogBase = ComponentBase<DialogProps>;

type FloatingAlertBase = ComponentBase<FloatingAlertComponentProps>;

type ToastBase = ComponentBase<ToastComponentProps>;

type LegacyComponentSlotProps<T extends Cons> = (
	PublicNonVueOptionalNonFunctionProps<InstanceType<T>>
	& {
		[K in keyof PublicNonVueOptionalNonFunctionProps<InstanceType<T>> & string as `onUpdate:${K}`]?: (value: NonNullable<PublicNonVueOptionalNonFunctionProps<InstanceType<T>>[K]>) => any;
	}
	& {
		[K in `on${string}`]?: (...args: any[]) => any;
	}
);

interface UIState {
	dialogs: DialogWithId[];
	floatingComponentsAnchor?: Raw<FloatingComponentsAnchor>;
	floatingAlerts: FloatingAlertWithId[];
	toasts: ToastWithId[];
}

interface UIActions {
	openDialog(dialog: Dialog): () => void;
	openFloatingAlert(alert: FloatingAlert): () => void;
	openToast(toast: Toast): () => void;
	closeDialog(): void;
	closeFloatingAlert(): void;
	closeToast(): void;
	setFloatingComponentsAnchor(anchor: FloatingComponentsAnchor): () => void;
}

export type ComponentContent = ComponentValue<ComponentSlotSource[] | ComponentSlotSource | Slot | string | undefined>;

export type ComponentValue<T> = MaybeRef<T> | ComputedRef<T>;

export interface ComponentSlot<T extends ComponentTag = ComponentTag> {
	tag?: ComponentValue<T>;
	props?: ComponentValue<ComponentSlotProps<T>>;
	slot?: ComponentValue<ComponentSlotSource[] | ComponentSlotSource | string>;
}

export type ComponentSlotProps<T extends ComponentTag> = (
	(
		T extends Cons
			? LegacyComponentSlotProps<T>
			: T extends Component
				? ComponentInstance<T>['$props']
				// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				: {}
	)
	& HTMLAttributes
);

export type ComponentSlotSource = ComponentValue<ComponentSlot>;

export type ComponentTag = string | Component | Cons;

export interface Dialog extends DialogBase {
	header?: ComponentContent;
	body: ComponentContent;
	footer?: ComponentContent;
	/** Keeps an eligible covered dialog visible as an inert underlay. */
	keepVisibleWhenCovered?: ComponentValue<boolean>;
}

export type DialogWithId = ComponentWithId<Dialog>;

export type FloatingAlertWithId = ComponentWithId<FloatingAlert>;

export interface FloatingAlert extends FloatingAlertBase {
	header: ComponentContent;
	body: ComponentContent;
	footer?: ComponentContent;
}

export interface FloatingComponentsAnchor {
	element: ComponentValue<HTMLElement | null | undefined>;
}

export interface Toast extends ToastBase {
	action?: ComponentContent;
	body: ComponentContent;
}

export type ToastWithId = ComponentWithId<Toast>;

let nextUIItemId = 0;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
const useUIStore = defineStore<'ui', UIState, {}, UIActions>(
	'ui',
	{
		state: () => ({
			dialogs: [],
			floatingComponentsAnchor: undefined,
			floatingAlerts: [],
			toasts: [],
		}),
		actions: {
			openDialog(dialog) {
				const typedDialog = dialog as typeof this.dialogs[number];
				nextUIItemId += 1;
				typedDialog.id = `dialog-${nextUIItemId}`;
				const openedDialog = markRaw(typedDialog);
				this.dialogs.push(openedDialog);

				return () => {
					const index = this.dialogs.indexOf(openedDialog);

					if (index !== -1) {
						this.dialogs.splice(
							index,
							1,
						);
					}
				};
			},
			setFloatingComponentsAnchor(anchor) {
				const openedAnchor = markRaw(anchor);
				this.floatingComponentsAnchor = openedAnchor;

				return () => {
					if (this.floatingComponentsAnchor === openedAnchor) {
						this.floatingComponentsAnchor = undefined;
					}
				};
			},
			openFloatingAlert(alert) {
				const typedAlert = alert as typeof this.floatingAlerts[number];
				nextUIItemId += 1;
				typedAlert.id = `floating-alert-${nextUIItemId}`;
				const openedAlert = markRaw(typedAlert);
				this.floatingAlerts.push(openedAlert);

				return () => {
					const index = this.floatingAlerts.indexOf(openedAlert);

					if (index !== -1) {
						this.floatingAlerts.splice(
							index,
							1,
						);
					}
				};
			},
			openToast(toast) {
				const typedToast = toast as typeof this.toasts[number];
				nextUIItemId += 1;
				typedToast.id = `toast-${nextUIItemId}`;
				const openedToast = markRaw(typedToast);
				this.toasts.push(openedToast);

				return () => {
					const index = this.toasts.indexOf(openedToast);

					if (index !== -1) {
						this.toasts.splice(
							index,
							1,
						);
					}
				};
			},
			closeDialog() {
				this.dialogs.pop();
			},
			closeFloatingAlert() {
				this.floatingAlerts.pop();
			},
			closeToast() {
				this.toasts.pop();
			},
		},
	},
);

function componentSlot<T extends ComponentTag = string>(slot: ComponentSlot<T>): ComponentSlot<T> {
	return slot;
}

/**
 * Registers a virtual positioning box for globally rendered floating alerts
 * and toast messages for the current Vue owner lifecycle.
 *
 * @param element Element ref or computed element ref that should become the floating feedback anchor.
 */
function useGlobalFloatingComponentsAnchor(element: FloatingComponentsAnchor['element']): void {
	const uiStore = useUIStore();
	let unsetAnchor: (() => void) | undefined = undefined;

	onMounted(() => {
		unsetAnchor = uiStore.setFloatingComponentsAnchor({
			element,
		});
	});

	onBeforeUnmount(() => {
		unsetAnchor?.();
		unsetAnchor = undefined;
	});
}

export {
	componentSlot,
	useGlobalFloatingComponentsAnchor,
	useUIStore,
};
